import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { de } from 'date-fns/locale';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Upload as UploadIcon,
  Description as DocumentIcon,
  Delete as DeleteIcon,
  Payments as PaymentsIcon,
  CreditCard as CreditCardIcon,
  MonetizationOn as MonetizationOnIcon,
  PaymentOutlined as PayPalIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { fetchVehicleById, createVehicle, updateVehicle } from '../../redux/slices/vehiclesSlice';
import { fetchDrivers } from '../../redux/slices/driversSlice';
import { VEHICLE_TYPE_OPTIONS, CAPABILITIES_PER_TYPE, SPECIAL_CAPABILITIES, migrateSpecialVehicleType } from '../../config/vehicleTypes';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const DocumentCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const UploadButton = styled('label')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  border: `1px dashed ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.primary.main,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  width: '100%',
  height: '56px',
  marginBottom: theme.spacing(2)
}));

const HiddenInput = styled('input')({
  display: 'none',
});

const VehicleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isEditMode = !!id;
  const fromPath = location.state?.from;
  
  const { currentVehicle, isLoading: vehicleLoading, error: vehicleError } = useSelector(state => state.vehicles);
  const { drivers, isLoading: driversLoading } = useSelector(state => state.drivers);
  
  const [formData, setFormData] = useState({
    vehicleNumber: '',
    licensePlate: '',
    model: '',
    vehicleType: 1,
    specialCapabilities: [],
    paymentMethods: 1, // Standardmäßig nur Barzahlung
    status: 'available', // Standardmäßig verfügbar
    inspectionDate: null,
    tuvDate: null,
    documents: []
  });

  const [formErrors, setFormErrors] = useState({
    vehicleNumber: '',
    licensePlate: '',
    model: '',
    vehicleType: '',
    paymentMethods: '',
    inspectionDate: '',
    tuvDate: ''
  });
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchVehicleById(id));
    }
  }, [dispatch, id, isEditMode]);
  
  useEffect(() => {
    if (isEditMode && currentVehicle) {
      // Sicherstellen, dass alle Felder korrekt initialisiert werden
      setFormData({
        vehicleNumber: currentVehicle.vehicleNumber || '',
        licensePlate: currentVehicle.licensePlate || '',
        model: currentVehicle.model || '',
        vehicleType: currentVehicle.vehicleType || 1,
        specialCapabilities: currentVehicle.specialCapabilities || migrateSpecialVehicleType(currentVehicle),
        paymentMethods: currentVehicle.paymentMethods || 1,
        status: currentVehicle.status || 'available',
        inspectionDate: currentVehicle.inspectionDate ? new Date(currentVehicle.inspectionDate) : null,
        tuvDate: currentVehicle.tuvDate ? new Date(currentVehicle.tuvDate) : null,
        documents: Array.isArray(currentVehicle.documents) ? currentVehicle.documents : []
      });
      
      // Debug-Information zum besseren Verständnis des Datenformats
      console.log('Geladene Fahrzeugdaten:', currentVehicle);
    }
  }, [currentVehicle, isEditMode]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...formData, [name]: value };

    setFormData(updatedData);
    
    // Fehler zurücksetzen, wenn der Benutzer ein Feld ändert
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  const handleDateChange = (name) => (date) => {
    setFormData({
      ...formData,
      [name]: date
    });
    
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  const handleDocumentUpload = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadingDocument(true);
      
      try {
        // Simuliere Upload-Verzögerung
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // In einer realen Anwendung würde hier der Upload zur Firebase Storage erfolgen
        // Für dieses Beispiel fügen wir einfach Dokument-Objekte zur Liste der Dokumente hinzu
        const newDocuments = Array.from(e.target.files).map(file => {
          // Erstelle ein standardisiertes Dokument-Objekt
          return {
            name: file.name,
            type: file.type,
            size: file.size,
            uploadDate: new Date().toISOString(),
            url: `https://example.com/documents/${file.name}`
          };
        });
        
        // Sicherstellen, dass documents ein Array ist, bevor wir es aktualisieren
        const currentDocuments = Array.isArray(formData.documents) ? formData.documents : [];
        
        setFormData({
          ...formData,
          documents: [...currentDocuments, ...newDocuments]
        });
        
      } catch (error) {
        console.error('Fehler beim Hochladen des Dokuments:', error);
      } finally {
        setUploadingDocument(false);
      }
    }
  };
  
  const handleRemoveDocument = (index) => {
    const updatedDocuments = [...formData.documents];
    updatedDocuments.splice(index, 1);
    setFormData({
      ...formData,
      documents: updatedDocuments
    });
  };
  
  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };
    
    // Kennzeichen validieren
    if (!formData.licensePlate) {
      newErrors.licensePlate = 'Kennzeichen ist erforderlich';
      valid = false;
    }
    
    // Modell validieren
    if (!formData.model) {
      newErrors.model = 'Modell ist erforderlich';
      valid = false;
    }
    
    // Fahrzeugtyp validieren
    if (!formData.vehicleType || ![1, 2, 3, 4].includes(Number(formData.vehicleType))) {
      newErrors.vehicleType = 'Wählen Sie einen gültigen Fahrzeugtyp';
      valid = false;
    }
    
    // Zahlungsmethoden validieren
    if (!formData.paymentMethods || ![1, 2, 3].includes(Number(formData.paymentMethods))) {
      newErrors.paymentMethods = 'Wählen Sie eine gültige Zahlungsmethode';
      valid = false;
    }
    
    // Datumsfelder müssen nicht validiert werden, da sie null sein dürfen
    
    setFormErrors(newErrors);
    return valid;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Kapazität basierend auf Fahrzeugtyp setzen - wird in der DB automatisch gesetzt
      const vehicleType = parseInt(formData.vehicleType);
      
      const vehicleData = {
        vehicleNumber: formData.vehicleNumber.trim(),
        licensePlate: formData.licensePlate,
        model: formData.model,
        vehicleType: vehicleType,
        specialCapabilities: formData.specialCapabilities || [],
        paymentMethods: parseInt(formData.paymentMethods),
        status: formData.status,
        inspectionDate: formData.inspectionDate ? formData.inspectionDate.toISOString() : null,
        tuvDate: formData.tuvDate ? formData.tuvDate.toISOString() : null,
        documents: formData.documents || []
      };
      
      if (isEditMode) {
        // Update existing vehicle
        dispatch(updateVehicle({
          id,
          ...vehicleData
        })).then((result) => {
          if (!result.error) {
            navigate(fromPath || `/fahrzeuge/${id}`);
          }
        });
      } else {
        // Create new vehicle
        dispatch(createVehicle(vehicleData)).then((result) => {
          if (!result.error) {
            navigate('/fahrzeuge');
          }
        });
      }
    }
  };
  
  const handleCancel = () => {
    if (isEditMode) {
      navigate(fromPath || `/fahrzeuge/${id}`);
    } else {
      navigate('/fahrzeuge');
    }
  };
  
  const isLoading = vehicleLoading;
  
  if (isLoading && isEditMode) {
    return <Loading message="Daten werden geladen..." />;
  }
  
  if (vehicleError) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Daten"
        message={vehicleError}
        onRetry={() => isEditMode && dispatch(fetchVehicleById(id))}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title={isEditMode ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug anlegen'}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrzeuge', path: '/fahrzeuge' },
          { label: isEditMode ? 'Bearbeiten' : 'Neu', path: isEditMode ? `/fahrzeuge/${id}/bearbeiten` : '/fahrzeuge/neu' }
        ]}
      />
      
      <form onSubmit={handleSubmit}>
        {/* Pflichtfelder */}
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Pflichtfelder
          </Typography>
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Die Anzahl der Sitzplätze wird automatisch anhand des Fahrzeugtyps bestimmt.
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                id="vehicleNumber"
                name="vehicleNumber"
                label="Fahrzeugnummer"
                value={formData.vehicleNumber}
                onChange={handleChange}
                error={!!formErrors.vehicleNumber}
                helperText={formErrors.vehicleNumber || "Optional, z.B. 23 oder 023"}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                id="model"
                name="model"
                label="Fahrzeug Modell"
                value={formData.model}
                onChange={handleChange}
                error={!!formErrors.model}
                helperText={formErrors.model || "z.B. Audi Q5, Mercedes E-Klasse"}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                id="licensePlate"
                name="licensePlate"
                label="Kennzeichen"
                value={formData.licensePlate}
                onChange={handleChange}
                error={!!formErrors.licensePlate}
                helperText={formErrors.licensePlate || "z.B. PI-JC 786"}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel id="vehicleType-label">Fahrzeugtyp</InputLabel>
                <Select
                  labelId="vehicleType-label"
                  id="vehicleType"
                  name="vehicleType"
                  value={formData.vehicleType}
                  onChange={handleChange}
                  label="Fahrzeugtyp"
                  error={!!formErrors.vehicleType}
                >
                  {VEHICLE_TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                {formErrors.vehicleType && (
                  <FormHelperText error>{formErrors.vehicleType}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="specialCapabilities-label">Spezialfähigkeiten</InputLabel>
                <Select
                  labelId="specialCapabilities-label"
                  id="specialCapabilities"
                  multiple
                  value={formData.specialCapabilities}
                  onChange={(e) => setFormData({ ...formData, specialCapabilities: e.target.value })}
                  label="Spezialfähigkeiten"
                  renderValue={(selected) => selected.map(s => SPECIAL_CAPABILITIES[s] || s).join(', ')}
                >
                  {Object.entries(SPECIAL_CAPABILITIES).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>Optional: Spezialfähigkeiten dieses Fahrzeugs</FormHelperText>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel id="paymentMethods-label">Zahlungsmöglichkeiten</InputLabel>
                <Select
                  labelId="paymentMethods-label"
                  id="paymentMethods"
                  name="paymentMethods"
                  value={formData.paymentMethods}
                  onChange={handleChange}
                  label="Zahlungsmöglichkeiten"
                  error={!!formErrors.paymentMethods}
                >
                  <MenuItem value={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MonetizationOnIcon color="primary" sx={{ mr: 1 }} />
                      Nur Barzahlung
                    </Box>
                  </MenuItem>
                  <MenuItem value={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MonetizationOnIcon color="primary" sx={{ mr: 1 }} />
                      <CreditCardIcon color="primary" sx={{ mr: 1 }} />
                      Bar- und Kartenzahlung
                    </Box>
                  </MenuItem>
                  <MenuItem value={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MonetizationOnIcon color="primary" sx={{ mr: 1 }} />
                      <CreditCardIcon color="primary" sx={{ mr: 1 }} />
                      <PayPalIcon color="primary" sx={{ mr: 1 }} />
                      Bar-, Karten- und PayPal-Zahlung
                    </Box>
                  </MenuItem>
                </Select>
                {formErrors.paymentMethods && (
                  <FormHelperText error>{formErrors.paymentMethods}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            {/* Status-Feld ausgeblendet, da standardmäßig auf "verfügbar" gesetzt */}
          </Grid>
        </FormPaper>
        
        {/* Optionale Felder */}
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Optionale Informationen
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                <DatePicker
                  label="TÜV bis"
                  value={formData.tuvDate}
                  onChange={handleDateChange('tuvDate')}
                  slots={{
                    textField: TextField
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.tuvDate,
                      helperText: formErrors.tuvDate
                    }
                  }}
                  format="dd.MM.yyyy"
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                <DatePicker
                  label="Inspektion am"
                  value={formData.inspectionDate}
                  onChange={handleDateChange('inspectionDate')}
                  slots={{
                    textField: TextField
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.inspectionDate,
                      helperText: formErrors.inspectionDate
                    }
                  }}
                  format="dd.MM.yyyy"
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </FormPaper>
        
        {/* Dokumente */}
        <FormPaper elevation={2}>
          <Typography variant="h6" gutterBottom>
            Fahrzeugdokumente
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            {formData.documents.length > 0 ? (
              formData.documents.map((doc, index) => (
                <DocumentCard key={index} variant="outlined">
                  <CardContent sx={{ pb: 1 }}>
                    <Typography variant="subtitle1">
                      Dokument {index + 1}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-all' }}>
                      {typeof doc === 'object' ? doc.name || doc.url || 'Dokument' : doc}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <Button 
                      size="small" 
                      href={typeof doc === 'object' ? doc.url : doc} 
                      target="_blank"
                    >
                      Anzeigen
                    </Button>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => handleRemoveDocument(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                </DocumentCard>
              ))
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Keine Dokumente hochgeladen.
              </Alert>
            )}
          </Box>
          
          <UploadButton htmlFor="document-upload">
            <UploadIcon sx={{ mr: 1 }} />
            {uploadingDocument ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Dokument wird hochgeladen...
              </>
            ) : (
              'Dokument hochladen'
            )}
          </UploadButton>
          <HiddenInput
            id="document-upload"
            type="file"
            onChange={handleDocumentUpload}
            disabled={uploadingDocument}
            multiple
          />
        </FormPaper>
        
        {/* Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleCancel}
            startIcon={<CancelIcon />}
          >
            Abbrechen
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            disabled={isLoading}
          >
            {isEditMode ? 'Änderungen speichern' : 'Fahrzeug erstellen'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default VehicleForm;
